import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, CaretDown, CaretRight, CheckCircle,
  Warning, Sparkle, Target, Rocket, ChartLineUp, Megaphone, Code,
  RocketLaunch, Lightning,
  Cpu, Gauge, PlugsConnected, SolarPanel, Fan, ThermometerSimple, Lightbulb,
  Cube, Factory, Bank, Storefront, Broadcast, Package, Wrench, Star,
  Briefcase, Buildings, Shield, ChartBar, Robot, Broom, Headset,
  Envelope, MagnifyingGlass, DeviceMobile, Globe, Database, Lock,
} from "@phosphor-icons/react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import BecomePartnerDialog from "../widgets/BecomePartnerDialog";
import ConnectDialog from "../widgets/ConnectDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same set as ProductDetail + a few service-friendly picks. Admin edits the icon
// slug in the Detail Pages JSON editor (see schema hint).
const ICON_MAP = {
  lightning: Lightning, buildings: Buildings, shield: Shield, briefcase: Briefcase, sparkle: Sparkle,
  cpu: Cpu, gauge: Gauge, plugs: PlugsConnected, "plugs-connected": PlugsConnected,
  solar: SolarPanel, "solar-panel": SolarPanel, fan: Fan, thermometer: ThermometerSimple,
  lightbulb: Lightbulb, cube: Cube, factory: Factory, bank: Bank,
  storefront: Storefront, broadcast: Broadcast, package: Package, wrench: Wrench, star: Star,
  target: Target, rocket: Rocket, "rocket-launch": RocketLaunch, "chart-line": ChartLineUp,
  megaphone: Megaphone, code: Code, "chart-bar": ChartBar, robot: Robot,
  broom: Broom, headset: Headset, envelope: Envelope, "magnifying-glass": MagnifyingGlass,
  "device-mobile": DeviceMobile, globe: Globe, database: Database, lock: Lock,
};

const PARENT_ICONS = {
  "digital-marketing": Megaphone,
  "it-consulting":     Code,
  "ai-automation":     RocketLaunch,
};

const PARENT_LABELS = {
  "digital-marketing": "Digital Marketing",
  "it-consulting":     "IT Consulting",
  "ai-automation":     "AI Automation",
};

function FaqRow({ idx, q, a, open, onToggle }) {
  return (
    <li className={`transition-colors ${open ? "bg-slate-50" : ""}`}>
      <button type="button" onClick={onToggle}
        className="w-full text-start flex items-start gap-6 px-6 md:px-8 py-6 hover:bg-slate-50/60 transition"
        data-testid={`sp-faq-toggle-${idx}`}>
        <span className="text-[12px] tabular-nums tracking-wider font-semibold mt-1.5 shrink-0" style={{ color: "#6B7A8D" }}>
          [{String(idx + 1).padStart(2, "0")}]
        </span>
        <span className="flex-1 font-heading font-semibold text-[17px] md:text-[19px] leading-snug tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.015em" }}>{q}</span>
        <CaretDown size={16} weight="bold" className={`mt-1.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} style={{ color: "#1B6FE0" }} />
      </button>
      <div className={`grid transition-all duration-400 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="px-6 md:px-8 pb-6 pl-[3.5rem] md:pl-[5rem] text-[14.5px] md:text-[14.5px] text-[#1C2B3A] leading-[1.85] font-light">{a}</div>
        </div>
      </div>
    </li>
  );
}

function Empty({ message }) {
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-24 min-h-[60vh] text-center">
      <h1 className="font-heading font-bold text-3xl md:text-4xl" style={{ color: "#0F2A6E" }}>Service not found</h1>
      <p className="mt-4 text-[#6B7A8D]">{message}</p>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="anim anim-up font-heading font-bold text-2xl sm:text-4xl lg:text-[44px] tracking-tight leading-[1.1]"
      style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
      {children}
    </h2>
  );
}

// Small badge — shows a Phosphor icon when `icon` matches a known key.
// Falls back to a neutral hyphen (no numeric chip) when no icon is set — this
// removes the "01/02/03" visual that the user asked us to replace.
function CoreBadge({ icon, size = "lg" }) {
  const key = String(icon || "").trim().toLowerCase();
  const Icon = ICON_MAP[key];
  const dims = size === "sm"
    ? "w-9 h-9 rounded-md"
    : "w-11 h-11 rounded-lg";
  const iconSize = size === "sm" ? 16 : 20;
  return (
    <span
      className={`inline-flex items-center justify-center shrink-0 ${dims}`}
      style={{ background: "rgba(27,111,224,0.10)", color: "#1B6FE0" }}
    >
      {Icon
        ? <Icon size={iconSize} weight="duotone" />
        : <Sparkle size={iconSize} weight="duotone" />}
    </span>
  );
}

export default function ServiceDetail() {
  const params = useParams();
  const parentSlug = params.parent || null;
  const slug = params.slug;
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const url = parentSlug
      ? `${API}/service-pages/${parentSlug}/${slug}`
      : `${API}/service-pages/${slug}`;
    axios.get(url)
      .then((r) => { if (!cancelled) setPage(r.data.page); })
      .catch(() => { if (!cancelled) setError("This service page isn't published yet."); });
    return () => { cancelled = true; };
  }, [parentSlug, slug]);

  useEffect(() => {
    if (page?.seo_title) document.title = page.seo_title;
    if (page?.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = page.meta_description;
    }
  }, [page]);

  if (error) return <><Header /><Empty message={error} /><Footer /></>;
  if (!page) return <><Header /><div className="min-h-[60vh] flex items-center justify-center text-[#6B7A8D]">Loading…</div><Footer /></>;

  const p = page;
  const effectiveParent = p.parent_slug || parentSlug;
  const ParentIcon = PARENT_ICONS[effectiveParent || slug] || Sparkle;
  const isSub = Boolean(p.parent_slug);

  const useCasesAreObjects = (p.use_cases || []).length > 0 && typeof p.use_cases[0] === "object";

  return (
    <>
      <Header />
      <main className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden text-white py-14 sm:py-20 md:py-28"
          style={{ background: "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 55%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "rgba(27,111,224,0.35)" }} />
          <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
            <nav className="flex items-center gap-2 text-[12.5px] text-blue-200/80 mb-5 sm:mb-6 flex-wrap" data-testid="sp-breadcrumbs">
              <Link to="/#services" className="hover:text-white">Services</Link>
              {isSub && (<>
                <CaretRight size={11} />
                <Link to={`/services/${p.parent_slug}`} className="hover:text-white">{PARENT_LABELS[p.parent_slug] || p.parent_slug}</Link>
              </>)}
            </nav>
            <div className="grid lg:grid-cols-12 gap-8 md:gap-10 items-center">
              <div className="lg:col-span-9">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)" }}>
                    <ParentIcon size={20} weight="duotone" color="#FFFFFF" />
                  </div>
                  <span className="text-[11.5px] uppercase tracking-[0.2em] text-blue-200/80 font-semibold break-words">
                    {isSub ? `${PARENT_LABELS[p.parent_slug]} → ${slug.replace(/-/g, " ")}` : (PARENT_LABELS[slug] || "OpSys Digital")}
                  </span>
                </div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[56px] tracking-tight leading-[1.05]"
                  style={{ letterSpacing: "-0.025em" }} data-testid="sp-hero-h1">{p.h1}</h1>
                {p.hero_copy && (
                  <p className="mt-5 sm:mt-6 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 leading-relaxed font-light max-w-3xl">{p.hero_copy}</p>
                )}
                <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
                  <button onClick={() => document.getElementById("sp-final-cta")?.scrollIntoView({ behavior: "smooth" })}
                    data-testid="sp-cta-primary"
                    className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-md text-white hover:brightness-110 font-medium text-[14px] sm:text-[14.5px]" style={{ background: "#1B6FE0" }}>
                    Request a Consultation <ArrowRight size={15} weight="bold" />
                  </button>
                  {!isSub && (
                    <a href="#sp-sub-services" data-testid="sp-cta-explore"
                      className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-md border border-white/40 text-white hover:bg-white/10 font-medium text-[14px] sm:text-[14.5px]">
                      Explore capabilities
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge + Help — 2 cols */}
        {(p.challenge || p.help) && (
          <section className="py-12 sm:py-16 md:py-24 bg-white">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-5 sm:gap-6 md:gap-8">
              {p.challenge && (
                <div className="rounded-2xl p-6 sm:p-7 md:p-9 border border-slate-200" style={{ background: "#F5F7FA" }}>
                  <div className="flex items-center gap-2.5">
                    <Warning size={18} weight="duotone" style={{ color: "#1B6FE0" }} />
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#6B7A8D" }}>The business challenge</div>
                  </div>
                  <p className="mt-4 text-[14px] sm:text-[14.5px] md:text-[15.5px] text-[#1C2B3A] leading-[1.85] font-light">{p.challenge}</p>
                </div>
              )}
              {p.help && (
                <div className="rounded-2xl p-6 sm:p-7 md:p-9 border border-[#1B6FE0]/25 bg-white shadow-sm">
                  <div className="flex items-center gap-2.5">
                    <Sparkle size={18} weight="duotone" style={{ color: "#1B6FE0" }} />
                    <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#1B6FE0" }}>How OpSys Digital helps</div>
                  </div>
                  <p className="mt-4 text-[14px] sm:text-[14.5px] md:text-[15.5px] text-[#1C2B3A] leading-[1.85] font-light">{p.help}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* CORE AREAS (main pages) */}
        {(p.core_areas || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24" style={{ background: "#F5F7FA" }}>
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <SectionHeading>Core service areas</SectionHeading>
              <div className="mt-8 sm:mt-10 grid md:grid-cols-2 gap-4 sm:gap-5 stagger anim">
                {p.core_areas.map((c, i) => (
                  <div key={i} className="anim anim-up card-lift p-5 sm:p-7 md:p-8 rounded-2xl bg-white border border-slate-200 hover:border-[#1B6FE0]/40 hover:shadow-md transition-all duration-300" data-testid={`sp-area-${i}`}>
                    <div className="flex items-center gap-3">
                      <CoreBadge icon={c.icon} />
                      <h3 className="font-heading font-semibold text-[17px] sm:text-[19px] md:text-[21px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>{c.title}</h3>
                    </div>
                    <p className="mt-3 text-[14px] md:text-[14.5px] text-[#1C2B3A] leading-relaxed font-light">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SUB-SERVICES LINKS (main pages) */}
        {(p.sub_services || []).length > 0 && (
          <section id="sp-sub-services" className="py-12 sm:py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <SectionHeading>Explore our {PARENT_LABELS[slug] || "services"}</SectionHeading>
              <p className="mt-4 text-[14px] sm:text-[15px] text-[#6B7A8D] font-light max-w-2xl">Dive deeper into each capability — dedicated pages cover the challenge, how we help, use cases, deliverables and outcomes.</p>
              <div className="mt-8 sm:mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 stagger anim">
                {p.sub_services.map((s, i) => (
                  <Link key={i} to={`/services/${slug}/${s.slug}`}
                    className="anim anim-up card-lift group p-5 sm:p-6 md:p-7 rounded-2xl border border-slate-200 hover:border-[#1B6FE0]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 bg-white flex flex-col"
                    data-testid={`sp-sub-${s.slug}`}>
                    <div className="flex items-center gap-3">
                      <CoreBadge icon={s.icon} size="sm" />
                      <h3 className="font-heading font-semibold text-[16px] sm:text-[17px] md:text-[18.5px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>{s.title}</h3>
                    </div>
                    <p className="mt-2 text-[13.5px] text-[#1C2B3A] leading-relaxed font-light flex-1">{s.blurb}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-medium transition group-hover:gap-2.5" style={{ color: "#1B6FE0" }}>
                      Read more <ArrowRight size={13} weight="bold" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* USE CASES */}
        {(p.use_cases || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24" style={{ background: p.core_areas ? "#F5F7FA" : "#FFFFFF" }}>
            <div className="max-w-6xl mx-auto px-4 lg:px-8">
              <SectionHeading>Use cases</SectionHeading>
              <div className={`mt-6 sm:mt-8 grid gap-4 ${useCasesAreObjects ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
                {p.use_cases.map((uc, i) => (
                  <div key={i}
                    className="anim anim-up p-5 md:p-6 rounded-xl border border-slate-200 bg-white hover:border-[#1B6FE0]/40 transition"
                    data-testid={`sp-usecase-${i}`}>
                    {useCasesAreObjects ? (
                      <>
                        <h3 className="font-semibold text-[14.5px]" style={{ color: "#0F2A6E" }}>{uc.title}</h3>
                        <p className="mt-2 text-[13.5px] text-[#1C2B3A] leading-relaxed font-light">{uc.desc}</p>
                      </>
                    ) : (
                      <div className="flex items-start gap-2.5">
                        <Target size={16} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                        <span className="text-[14px] text-[#1C2B3A]">{uc}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* WHAT'S INCLUDED (sub-service pages) */}
        {(p.included || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24 bg-white">
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <SectionHeading>What&apos;s included</SectionHeading>
              <ul className="mt-6 sm:mt-8 grid sm:grid-cols-2 gap-3 stagger anim">
                {p.included.map((it, i) => (
                  <li key={i} className="anim anim-up p-4 rounded-xl border border-slate-200 bg-white flex items-start gap-2.5" data-testid={`sp-inc-${i}`}>
                    <CheckCircle size={16} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                    <span className="text-[14px] text-[#1C2B3A]">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* BUSINESS OUTCOMES */}
        {(p.outcomes || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24" style={{ background: "#0B1F4D" }}>
            <div className="max-w-6xl mx-auto px-4 lg:px-8 text-white">
              <div className="flex items-center gap-2.5 mb-4">
                <ChartLineUp size={20} weight="duotone" style={{ color: "#1B6FE0" }} />
                <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-blue-200/85">Business outcomes</div>
              </div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]" style={{ letterSpacing: "-0.025em" }}>Outcomes you can measure</h2>
              <ul className="mt-8 sm:mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger anim">
                {p.outcomes.map((o, i) => (
                  <li key={i} className="anim anim-up p-5 rounded-xl bg-white/[0.04] border border-white/10 hover:border-[#1B6FE0]/50 hover:bg-white/[0.08] transition flex items-start gap-2.5" data-testid={`sp-outcome-${i}`}>
                    <Lightning size={16} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                    <span className="text-[14px] text-slate-100/95 leading-relaxed">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* HOW WE DELIVER (main pages) */}
        {(p.how_we_deliver || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24 bg-white">
            <div className="max-w-6xl mx-auto px-4 lg:px-8">
              <SectionHeading>How we deliver</SectionHeading>
              <ol className="mt-8 sm:mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-3 stagger anim">
                {p.how_we_deliver.map((h, i) => (
                  <li key={i} className="anim anim-up card-lift p-5 md:p-6 rounded-2xl border border-slate-200 bg-white" data-testid={`sp-step-${i}`}>
                    <div className="text-[11px] font-mono tabular-nums" style={{ color: "#1B6FE0" }}>STEP {i + 1}</div>
                    <div className="mt-2 font-heading font-semibold text-[15.5px]" style={{ color: "#0F2A6E" }}>{h.step}</div>
                    <p className="mt-2 text-[13px] text-[#1C2B3A] leading-relaxed font-light">{h.desc}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* FAQs */}
        {(p.faqs || []).length > 0 && (
          <section className="py-12 sm:py-16 md:py-24" style={{ background: "#F5F7FA" }}>
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <SectionHeading>Frequently asked questions</SectionHeading>
              <ul className="mt-8 sm:mt-10 rounded-3xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                {p.faqs.map((f, i) => (
                  <FaqRow key={i} idx={i} q={f.q} a={f.a}
                    open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section id="sp-final-cta" className="py-14 sm:py-20 md:py-24 relative overflow-hidden text-white"
          style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="relative max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]" style={{ letterSpacing: "-0.025em" }}>
              {p.final_cta?.heading || "Ready to get started?"}
            </h2>
            {p.final_cta?.body && (
              <p className="mt-5 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 font-light leading-relaxed max-w-2xl mx-auto">{p.final_cta.body}</p>
            )}
            <div className="mt-8 sm:mt-9 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => setConnectOpen(true)} data-testid="sp-final-cta-primary"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md bg-white text-[#0F2A6E] font-semibold hover:bg-blue-50">
                {p.final_cta?.text || "Request a Consultation"} <ArrowRight size={15} weight="bold" />
              </button>
              <button onClick={() => setPartnerOpen(true)} data-testid="sp-final-cta-partner"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md border border-white/40 text-white font-medium hover:bg-white/10">
                Become a partner
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BecomePartnerDialog open={partnerOpen} onOpenChange={setPartnerOpen} />
      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </>
  );
}
